import { EXPENSE_REPORT_MUTATIONS } from '@/state/mutation-types'

/**
 * Sum every expense line of the given reports, grouped by the currency the line is
 * denominated in. Amounts are stored in minor units, so each total is divided by 100.
 *
 * An expense line always carries its own currency. On the off chance one is missing, it is
 * grouped under '?' rather than assumed to be in the currency of anything around it.
 * Totals are never mixed across currencies, as there is no exchange rate available on this page.
 */
const sumByCurrency = (reports) => {
  const totals = {}

  reports.forEach((report) => {
    report.expenses.forEach((expense) => {
      const currency = expense.currency || '?'

      totals[currency] = (totals[currency] ?? 0) + expense.amount
    })
  })

  return Object.entries(totals).map(([currency, amount]) => ({
    currency,
    amount: Number(amount / 100).toFixed(2),
  }))
}

export default {
  state: {
    reports: [],
  },
  mutations: {
    [EXPENSE_REPORT_MUTATIONS.SET_REPORTS](state, expenseReports) {
      state.reports = [...expenseReports]
    },
    [EXPENSE_REPORT_MUTATIONS.ADD_REPORT](state, expenseReport) {
      state.reports = [...state.reports, { ...expenseReport, autoAddExpense: true }]
    },
    [EXPENSE_REPORT_MUTATIONS.UPDATE_REPORT](state, { reportId, updatedReport }) {
      const reportIndex = state.reports.findIndex((report) => report.localId === reportId)
      state.reports[reportIndex] = { ...updatedReport }
    },
    [EXPENSE_REPORT_MUTATIONS.DELETE_REPORT](state, reportId) {
      state.reports = [...state.reports.filter((report) => report.localId !== reportId)]
    },

    [EXPENSE_REPORT_MUTATIONS.ADD_LINE_ITEM](state, { reportId, lineItem }) {
      const reportIndex = state.reports.findIndex((report) => report.localId === reportId)
      if (reportIndex === -1) return
      const lineItems = [...state.reports[reportIndex].expenses]
      lineItems.push(lineItem)
      state.reports[reportIndex].expenses = lineItems
    },
    [EXPENSE_REPORT_MUTATIONS.UPDATE_LINE_ITEM](state, { reportId, lineItem }) {
      const reportIndex = state.reports?.findIndex((report) => report.localId === reportId)
      const expenseIndex = state.reports?.[reportIndex]?.expenses?.findIndex(
        (expense) => expense.localId === lineItem.localId,
      )
      if (state.reports?.[reportIndex]?.expenses?.[expenseIndex]) {
        state.reports[reportIndex].expenses[expenseIndex] = { ...lineItem }
      }
    },
    [EXPENSE_REPORT_MUTATIONS.DELETE_LINE_ITEM](state, { reportId, lineItemId }) {
      const reportIndex = state.reports.findIndex((report) => report.localId === reportId)
      state.reports[reportIndex].expenses = state.reports[reportIndex].expenses.filter(
        (expense) => expense.localId !== lineItemId,
      )
    },
  },
  getters: {
    activeExpenseReports: (state) =>
      state.reports.filter((report) =>
        report.bill
          ? !report.bill.isRejected && !report.bill.isPayed && !report.bill.isExpresspayed
          : true,
      ),
    reimbursedExpenseReports: (state) =>
      state.reports.filter((report) =>
        report.bill ? report.bill.isPayed || report.bill.isExpresspayed : false,
      ),
    billedExpensesTotals: (state, getters) => sumByCurrency(getters.reimbursedExpenseReports),
    unBilledExpensesTotals: (state, getters) => sumByCurrency(getters.activeExpenseReports),
  },
}
